import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import CreateTopicModal from './CreateTopicModal';
import styles from './MyTopics.module.css';
import logger from '../../../utils/logger';
const MyTopics = () => {
    const navigate = useNavigate();
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [categories, setCategories] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);

    useEffect(() => {
        loadData();
    }, [currentPage]);

    const loadData = async () => {
        try {
            
            setLoading(true);
            const [topicsResponse, categoriesResponse] = await Promise.all([
                AdminService.getMyForumTopics(currentPage, 20),
                AdminService.getForumCategories()
            ]);
            
            
            
            setTopics(topicsResponse.data || []);
            setTotalPages(topicsResponse.pages || 1);
            setCategories(categoriesResponse.data || []);
        } catch (err) {
            setError('Ошибка загрузки данных');
            logger.error('Ошибка загрузки моих тем:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditTopic = (topic) => {
        setEditingTopic(topic);
        setShowEditModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setEditingTopic(null);
    };

    const handleTopicUpdated = () => {
        setShowEditModal(false);
        setEditingTopic(null);
        loadData();
    };

    const handleToggleTopicStatus = async (topic) => {
        try {
            const newStatus = topic.status === 'открыта' ? 'закрыта' : 'открыта';
            await AdminService.updateTopicStatus(topic.id, newStatus);
            loadData(); // Перезагружаем список
        } catch (error) {
            logger.error('Ошибка изменения статуса темы:', error);
            alert('Ошибка изменения статуса темы');
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleViewTopic = (topicId) => {
        navigate(`/main/forum/topics/${topicId}`);
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Загрузка ваших тем...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <p>{error}</p>
                <button onClick={loadData} className={styles.retryButton}>
                    Попробовать снова
                </button>
            </div>
        );
    }

    return (
        <div className={styles.myTopicsContainer}>
            <div className={styles.header}>
                <button 
                    onClick={() => navigate('/main/forum')}
                    className={styles.backButton}
                >
                    ← Назад к форуму
                </button>
                <h1>Мои вопросы на форуме</h1>
            </div>

            <div className={styles.topicsList}>
                {topics.length === 0 ? (
                    <div className={styles.emptyState}>
                        <h3>У вас пока нет тем на форуме</h3>
                        <p>Создайте свой первый вопрос, чтобы начать обсуждение</p>
                        <button 
                            onClick={() => navigate('/main/forum')}
                            className={styles.createFirstTopic}
                        >
                            Задать первый вопрос
                        </button>
                    </div>
                ) : (
                    topics.map(topic => (
                        <MyTopicCard 
                            key={topic.id} 
                            topic={topic}
                            onEdit={() => handleEditTopic(topic)}
                            onToggleStatus={() => handleToggleTopicStatus(topic)}
                            onView={() => handleViewTopic(topic.id)}
                        />
                    ))
                )}
            </div>

            {totalPages > 1 && (
                <div className={styles.pagination}>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`${styles.pageButton} ${
                                page === currentPage ? styles.activePage : ''
                            }`}
                        >
                            {page}
                        </button>
                    ))}
                </div>
            )}

            {showEditModal && (
                <CreateTopicModal
                    categories={categories}
                    onClose={handleCloseModal}
                    onTopicCreated={handleTopicUpdated}
                    editTopic={editingTopic}
                />
            )}
        </div>
    );
};

// Компонент карточки темы пользователя
const MyTopicCard = ({ topic, onEdit, onToggleStatus, onView }) => {
    return (
        <div className={styles.topicCard}>
            <div className={styles.cardHeader}>
                <div className={styles.titleSection}>
                    <h3 className={styles.topicTitle} onClick={onView}>
                        {topic.is_pinned && <span className={styles.pinnedIcon}>📌</span>}
                        {topic.title}
                    </h3>
                    <span className={`${styles.topicStatus} ${
                        topic.status === 'закрыта' ? styles.closedStatus : styles.openStatus
                    }`}>
                        {topic.status}
                    </span>
                </div>
                <div className={styles.actions}>
                    <button 
                        onClick={onEdit}
                        className={styles.editButton}
                        title="Редактировать тему"
                    >
                        ✏️ Редактировать
                    </button>
                    <button 
                        onClick={onToggleStatus}
                        className={`${styles.statusButton} ${
                            topic.status === 'открыта' ? styles.closeButton : styles.openButton
                        }`}
                        title={topic.status === 'открыта' ? 'Закрыть тему' : 'Открыть тему'}
                    >
                        {topic.status === 'открыта' ? '🔒 Закрыть' : '🔓 Открыть'}
                    </button>
                </div>
            </div>
            
            <div className={styles.topicMeta}>
                <span>Категория: {topic.category_name}</span>
                <span>Создано: {AdminService.formatForumDate(topic.created_at)}</span>
                {topic.updated_at !== topic.created_at && (
                    <span>Изменено: {AdminService.formatForumDate(topic.updated_at)}</span>
                )}
            </div>
            
            <p className={styles.topicPreview}>
                {AdminService.truncateText(topic.content, 200)}
            </p>
            
            <div className={styles.topicStats}>
                <span>👁 {topic.views_count} просмотров</span>
                <span>💬 {topic.replies_count} ответов</span>
                {topic.last_reply_at && (
                    <span className={styles.lastReply}>
                        Последний ответ: {AdminService.formatForumDate(topic.last_reply_at)}
                        {topic.last_reply_author && ` от ${topic.last_reply_author}`}
                    </span>
                )}
            </div>
        </div>
    );
};

export default MyTopics;